import { Box3, Vector2, Vector3 } from "three";
import { v2, v3 } from "oc-threejs/short3";
import * as Maths3 from "oc-threejs/Maths3";
import * as Maths2 from "oc-threejs/Maths2";
import convexHull from "monotone-convex-hull-2d";
import { Jaco } from "oc-jaco3d/arrangement/Jaco";
import {
  createEcke,
  gerichterZuUngerichtemGraph,
  GrundrissEcke,
  GrundrissKante,
  kantenZuStrecken,
  normalToKante,
  SegmentKante,
  Strecke2,
  streckenZuGraph,
  ungerichterZuGerichterGraph,
  unmarshalGraph,
} from "./grundrissFunctions";
import { Grundriss3d } from "../arrangement3d/Grundriss3d";
import {
  entferneUnnötigeEcken,
  fügeSchnittpunkteAlsEckenHinzu,
  verschmelzeGleicheEcken,
} from "./grundrissGerichteterGraph";

/**
 * Der Grundriss definiert die vom User gemalten Strecken des Zaunes. Die Strecken können beliebig angeordnet sein,
 * sich berühren, überkreuzen, können kolinear sein oder einzeln stehen.
 *
 * Intern wird der Grundriss als ungerichteter Graph ausgeführt.
 *
 * - Die convexHull bildet das umschließende Polygon und ist gleich der Grundstücksgrenze.
 * - Strecken können durch Aufbauten in eine oder mehrere Segmente unterteilt werden.
 * - Die Ecken und damit die Eckpfosten entsprechen nicht unbedingt den Punkten der Strecke.
 */
export class Grundriss extends Jaco {
  CLASS_NAME = "Grundriss";

  // ----------------------------------------------------------------------------------
  // persistierte Werte - Zustand, werden mit der Konfiguration gespeichert
  // ----------------------------------------------------------------------------------

  // ecken ist ein ungerichteter Graph aller Grundriss-Strecken
  ecken: GrundrissEcke[] = [];

  // ----------------------------------------------------------------------------------
  // abgeleitete Werte - d.h. sind letztlich nur berechnet (in update3d) und gecached, kein Zustand
  // Achtung, diese Werte muss man immer ganz besonders im Auge haben, kann sein,
  // dass sie fälschlich doch als Zustand dienen, und das führt dann zu schwierigen Bugs
  // ----------------------------------------------------------------------------------

  kanten: GrundrissKante[] = [];
  tore: SegmentKante[];
  kantenIdToChangeCount = new Map<number, number>();
  convexHull: GrundrissEcke[] = [];
  middlePoint = new Vector2();
  size = new Vector3();
  boundingBox = new Box3();

  constructor(parent: Jaco | null = null) {
    super(parent);
  }

  update3d() {
    super.update3d();
    this.fügeSchnittpunkteAlsEckenHinzuUndEntferneUnnötigeEcken();
    this.updateConvexHull();
    if (this.ecken.length > 0) {
      this.updateLiegtAufGrenze();
      this.updateMiddlePoint();
      this.updateDirectionAndNormals();
      this.sortGraph();
    }
    this.updateBoxAndSize();
  }

  sortGraph() {
    const idComparator = (a, b) => a.id - b.id;
    this.ecken.sort(idComparator);
    this.kanten.sort(idComparator);
  }

  updateBoxAndSize() {
    const points = this.convexHull.map((e) => Maths3.v2To3xz(e.punkt));
    this.boundingBox = new Box3().setFromPoints(points);
    if (this.boundingBox.getSize(v3()).length() < 0.001) {
      this.boundingBox.min.set(-10, 0, -10);
      this.boundingBox.max.set(+10, 0, +10);
    }
    this.boundingBox.max.y = 2;
    this.size = this.boundingBox.getSize(v3());
  }

  fügeSchnittpunkteAlsEckenHinzuUndEntferneUnnötigeEcken() {
    let ecken = ungerichterZuGerichterGraph(this.ecken);
    fügeSchnittpunkteAlsEckenHinzu(ecken);
    ecken = entferneUnnötigeEcken(ecken);
    ecken = verschmelzeGleicheEcken(ecken);
    ({ ecken: this.ecken, kanten: this.kanten } = gerichterZuUngerichtemGraph(ecken));
  }

  updateConvexHull() {
    const arrayPoints = this.ecken.map((e) => [e.punkt.x, e.punkt.y]);
    const hull = convexHull(arrayPoints);
    this.convexHull = hull.map((i) => this.ecken[i]);
  }

  updateLiegtAufGrenze() {
    const epsilon = 0.02;
    const liegtAufHull = (p: Vector2) => {
      const hull = this.convexHull;
      for (let i = 0; i < hull.length; i++) {
        const j = (i + 1) % hull.length;
        if (Maths2.distanceToLineSegment(p, [hull[i].punkt, hull[j].punkt]) < epsilon) {
          return true;
        }
      }
      return false;
    };
    this.ecken.forEach((e) => (e.liegtAufGrenze = liegtAufHull(e.punkt)));
  }

  updateMiddlePoint() {
    this.middlePoint = Maths2.middle(this.convexHull.map((e) => e.punkt));
  }

  updateDirectionAndNormals() {
    this.kanten.forEach((k) => {
      k.direction = Maths2.getDirection(k.ecken[0].punkt, k.ecken[1].punkt);
      k.normal = Maths2.orthogonalRight(k.direction);
      k.flippedNormal = false;
    });
    // Prüfe, ob alle Ecken auf einer Geraden liegen. Nur wenn sie es NICHT tun,
    // ist eine eindeutige Innen-/Außen-Seite über den middlePoint definierbar.
    // Kollineare Ecken (z.B. eine Strecke in Teilstücke geteilt) haben kein sinnvolles Innen/Außen.
    // Wichtig: Die Prüfung basiert auf den Ecken, nicht auf den Kantenrichtungen, da parallele
    // Kanten auf unterschiedlichen Geraden (Versatz) trotzdem ein Innen/Außen erzeugen.
    const alleEckenAufEinerGeraden = (() => {
      if (this.ecken.length <= 2) return true;
      const p0 = this.ecken[0].punkt;
      let dir: Vector2 | null = null;
      for (const e of this.ecken) {
        if (e.punkt.distanceTo(p0) > 0.01) {
          dir = Maths2.getDirection(p0, e.punkt);
          break;
        }
      }
      if (!dir) return true;
      return this.ecken.every((e) => {
        const diff = e.punkt.clone().sub(p0);
        return Math.abs(dir.cross(diff)) < 0.01;
      });
    })();
    if (!alleEckenAufEinerGeraden) {
      this.kanten.forEach((k) => {
        const middleNormal = normalToKante(
          [k.ecken[0].punkt, k.ecken[1].punkt],
          this.middlePoint
        ).negate();
        k.flippedNormal = k.normal.isEqualEpsilon(middleNormal, 0.01);
        k.normal = middleNormal;
      });
    }
    this.ecken.forEach((e) => {
      e.normalSum.set(0, 0);
      e.anliegendeKanten.forEach((v) => e.normalSum.add(v.normal));
    });
  }

  // updateNormals() {
  //   this.kanten.forEach((k) => {
  //     k.normal = normalToKante([k.ecken[0].punkt, k.ecken[1].punkt], this.middlePoint);
  //     k.flippedNormal = false;
  //   });
  //   if (this.kanten.length > 2) {
  //     this.kanten.forEach((k) => {
  //       const orthoLeft = Maths2.orthogonalLeft(
  //         Maths2.getDirection(k.ecken[0].punkt, k.ecken[1].punkt)
  //       );
  //       if (k.normal.dot(orthoLeft) > 0) {
  //         k.normal.negate();
  //         k.flippedNormal = true;
  //       }
  //     });
  //   }
  //   this.ecken.forEach((e) => {
  //     e.normalSum.set(0, 0);
  //     e.anliegendeKanten.forEach((v) => e.normalSum.add(v.normal));
  //   });
  // }

  // updateNormals() {
  //   this.kanten.forEach((k) => {
  //     const direction = Maths2.getDirection(k.ecken[0].punkt, k.ecken[1].punkt);
  //     k.normal = Maths2.orthogonalRight(direction);
  //     // das kann man wohl entfernen
  //     k.flippedNormal = true;
  //   });
  //
  //   // this.kanten.forEach((k) => {
  //   //   k.normal = normalToKante([k.ecken[0].punkt, k.ecken[1].punkt], this.middlePoint);
  //   //   k.flippedNormal = false;
  //   // });
  //   // if (this.kanten.length > 2) {
  //   //   this.kanten.forEach((k) => {
  //   //     const orthoLeft = Maths2.orthogonalLeft(
  //   //       Maths2.getDirection(k.ecken[0].punkt, k.ecken[1].punkt)
  //   //     );
  //   //     if (k.normal.dot(orthoLeft) > 0) {
  //   //       k.normal.negate();
  //   //       k.flippedNormal = true;
  //   //     }
  //   //   });
  //   // }
  //
  //   this.ecken.forEach((e) => {
  //     e.normalSum.set(0, 0);
  //     e.anliegendeKanten.forEach((v) => e.normalSum.add(v.normal));
  //   });
  // }

  clone() {
    const clone = new Grundriss(this.parent);
    clone.copy(this);
    return clone;
  }

  copy(other: Grundriss) {
    this.ecken = other.ecken.map((ecke) => createEcke(ecke.id, ecke.punkt));
    return this;
  }

  reset() {
    const fresh = new Grundriss(null);
    this.copy(fresh);
  }

  toJSON() {
    return {
      ecken: this.ecken.map(({ id, punkt }) => ({
        id,
        punkt: punkt.toJSON(),
      })),
      kanten: this.kanten.map(({ ecken }) => ({ ecken: ecken.map(({ id }) => id) })),
    } as any;
  }

  fromJSON(json: any = {}) {
    if (json?.ecken) {
      const ecken = json.ecken.map(({ id, punkt }: { id: number; punkt: any }) => ({
        id,
        punkt: v2().fromJSON(punkt),
      }));
      ({ ecken: this.ecken, kanten: this.kanten } = unmarshalGraph(ecken, json.kanten));
    } else {
      const strecken = json?.strecken.map((strecke) =>
        // TODO toReversed() klappt, aber warum??
        strecke.toReversed().map((p) => v2().fromJSON(p))
      );
      this.setStrecken(strecken);
    }
    return this;
  }

  setStandardForm(
    form: FormType,
    seitenLängen: number[] = this.getStandardFormSeitenLängen(form),
    zaunSeitenFüllen: boolean[] = undefined,
    compatibilityWithOldConfigurations = false
  ) {
    let points: Vector2[] = [];
    if (form == "Leer") {
      this.ecken = [];
      return;
    }

    let strecken = [];
    if (form == "Strecke") {
      const [a] = seitenLängen;
      points = [v2(-a / 2, 0), v2(a / 2, 0)];
      strecken = [points];
    } else if (form == "Eck") {
      const [a, b] = seitenLängen;
      let [p0, p1, p2] = (points = [v2(0, 0), v2(a, 0), v2(a, b)]);
      strecken = [
        [p0, p1],
        [p1, p2],
      ];
    } else {
      if (form == "Rechteck") {
        zaunSeitenFüllen = zaunSeitenFüllen ?? [true, true, true, true];
        const [a, b] = seitenLängen;
        points = [v2(0, 0), v2(0, a), v2(b, a), v2(b, 0)];
      } else if (form == "UForm") {
        zaunSeitenFüllen = zaunSeitenFüllen ?? [true, true, true, false];
        const [a, b] = seitenLängen;
        points = [v2(0, 0), v2(0, a), v2(b, a), v2(b, 0)];
      } else if (form == "LForm") {
        zaunSeitenFüllen = [true, true, true, true, true, true];
        const [a, b, c, d] = seitenLängen;
        points = [v2(0, 0), v2(a, 0), v2(a, b), v2(a + c, b), v2(a + c, b + d), v2(0, b + d)];
      } else {
        throw new Error("unknown Form " + form);
      }
      const middle = Maths2.middle(points);
      points.forEach((p) => p.sub(middle));
      for (let i = 0; i < points.length; i++) {
        const j = (i + 1) % points.length;
        const a = points[i].clone();
        const b = points[j].clone();
        if (zaunSeitenFüllen[i] !== false) {
          strecken.push([a, b]);
        }
      }
      if (compatibilityWithOldConfigurations) {
        if (form == "Rechteck") {
          strecken.forEach((s) =>
            s.forEach((p) => {
              p.rotateAround(v2(), (90 * Math.PI) / 180);
            })
          );
        } else if (form == "LForm") {
          strecken.forEach((s) =>
            s.forEach((p) => {
              p.y *= -1;
            })
          );
        }
      }
    }

    this.setStrecken(strecken);
    return this;
  }

  getStandardFormSeitenLängen(form: FormType) {
    if (form == "Leer") {
      return [];
    } else if (form == "Strecke") {
      return [10];
    } else if (form == "Rechteck" || form == "UForm") {
      return [20, 20];
    } else if (form == "LForm") {
      return [10, 10, 10, 10];
    } else if (form == "Eck") {
      return [10, 10];
    } else {
      throw new Error("unknown Form " + form);
    }
  }

  getSize() {
    return this.size.clone();
  }

  getBox() {
    return this.boundingBox.clone();
  }

  createJaco3d() {
    return new Grundriss3d(this);
  }

  setStrecken(strecken: Strecke2[]) {
    ({ ecken: this.ecken, kanten: this.kanten } = streckenZuGraph(strecken));
  }

  getStrecken(): Strecke2[] {
    return kantenZuStrecken(this.kanten);
  }
}

export type FormType = "Leer" | "Strecke" | "Rechteck" | "LForm" | "UForm" | "Eck";
